import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * One-time Terms-acceptance modal.
 *
 * Trigger: every authenticated mount checks `/api/auth/me/terms-status`.
 * If `needs_acceptance` is true, the modal renders blocking — only the
 * "I Agree and Continue" CTA can dismiss it. POSTs `/api/auth/me/accept-terms`
 * to record the timestamp + user + accepted version.
 *
 * Admins are exempt — they manage Terms versioning rather than accepting it.
 */
export default function TermsAcceptanceModal() {
  const { user } = useAuth();
  const [needs, setNeeds] = useState(false);
  const [version, setVersion] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user || user.is_admin) return;
    let cancelled = false;
    const check = () => {
      api
        .get("/auth/me/terms-status")
        .then(({ data }) => {
          if (cancelled) return;
          setVersion(data.current_version || "1.0");
          setNeeds(!!data.needs_acceptance);
        })
        .catch(() => {});
    };
    check();
    // External code (e.g., pre-checkout in Billing) can ask the modal to
    // re-check on demand by dispatching this custom event.
    window.addEventListener("regulens:terms-recheck", check);
    return () => {
      cancelled = true;
      window.removeEventListener("regulens:terms-recheck", check);
    };
  }, [user]);

  const accept = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/me/accept-terms");
      setNeeds(false);
    } catch {
      // Surface to the user so they don't think the click was no-op.
      toast.error("Could not record your acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user || user.is_admin || !needs) return null;

  return (
    <Dialog
      open={needs}
      onOpenChange={() => {
        /* Block ESC + outside-click dismissal — there is no skip. */
      }}
    >
      <DialogContent
        className="bg-[var(--rg-surface)] border-[var(--rg-sage)] text-[var(--rg-text-strong)] max-w-lg"
        data-testid="terms-acceptance-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[var(--rg-text-strong)]" data-testid="terms-modal-heading">
            Before You Continue
          </DialogTitle>
        </DialogHeader>

        <div className="text-sm text-[var(--rg-text)] leading-relaxed space-y-3">
          <p data-testid="terms-modal-summary">
            Regulens provides informational readiness scores only. Our scores
            are not legal advice and do not constitute compliance
            certification. By continuing you agree to our{" "}
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--rg-sage)] hover:underline font-semibold"
              data-testid="terms-modal-link-terms"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/refund-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--rg-sage)] hover:underline font-semibold"
              data-testid="terms-modal-link-refund"
            >
              Refund Policy
            </a>
            .
          </p>
          {version && (
            <p className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--rg-muted)]" data-testid="terms-modal-version">
              Active version · Terms v{version}
            </p>
          )}
        </div>

        <Button
          onClick={accept}
          disabled={submitting}
          className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold w-full mt-2"
          data-testid="terms-modal-accept"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
            </>
          ) : (
            "I Agree and Continue"
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
